import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DemoBookingForm from "../sections/DemoBookingForm";

const DemoBookingContext = createContext(null);

export function useDemoBooking() {
  const value = useContext(DemoBookingContext);
  if (!value) {
    throw new Error("useDemoBooking must be used within DemoBookingProvider");
  }
  return value;
}

function BookingDialog({ open, onClose }) {
  const dialogRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !open || dialog.open) return;
    dialog.showModal();
    dialog.scrollTop = 0;
  }, [open]);

  const requestClose = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="booking-dialog"
      aria-labelledby="booking-title"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) requestClose();
      }}
    >
      <div className="booking-sheet">
        <div className="dialog-bar">
          <button type="button" className="icon-btn" aria-label={t("form.close")} onClick={requestClose}>
            ×
          </button>
        </div>
        {open ? <DemoBookingForm /> : null}
      </div>
    </dialog>
  );
}

export function DemoBookingProvider({ children }) {
  const [open, setOpen] = useState(false);
  const openBooking = useCallback(() => setOpen(true), []);
  const closeBooking = useCallback(() => setOpen(false), []);
  const value = useMemo(
    () => ({ openBooking, closeBooking }),
    [openBooking, closeBooking]
  );

  return (
    <DemoBookingContext.Provider value={value}>
      {children}
      <BookingDialog open={open} onClose={closeBooking} />
    </DemoBookingContext.Provider>
  );
}
